"use client";

import { GameClassroomButton } from "@/components/feature/game-classroom-button";
import { GameShareSheet } from "@/components/feature/game-share-sheet";
import { TheaterRailIconButton } from "@/components/feature/theater-rail-icon-button";
import { TheaterRailTitle } from "@/components/feature/theater-rail-title";
import { VideoToggleButton } from "@/components/feature/video-toggle-button";
import { Button } from "@/components/ui/button";
import { useFullscreenContext } from "@/contexts/fullscreen-context";
import { useShareLink } from "@/hooks/use-share-link";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { DEFAULT_CLASSROOM_BODY, classroomShareUrlForPage } from "@/lib/google-classroom-share";
import { cn } from "@/lib/utils";
import {
  ArrowsInSimpleIcon,
  ArrowsOutSimpleIcon,
  ExportIcon,
  Info,
  XIcon,
} from "@phosphor-icons/react";
import { useCallback, useState } from "react";

interface GameHeaderProps {
  title: string;
  iconUrl?: string | null;
  hasVideo?: boolean;
  isShowingVideo?: boolean;
  onVideoToggle?: () => void;
  hasMetadata?: boolean;
  onOpenMetadata?: () => void;
  shareUrl?: string;
  shareTitle?: string;
  onClose?: () => void;
  /** When false, the page already server-renders the document `<h1>`. */
  documentHeading?: boolean;
}

function GameIcon({
  title,
  iconUrl,
  hasMetadata,
  onOpenMetadata,
}: {
  title: string;
  iconUrl?: string | null;
  hasMetadata: boolean;
  onOpenMetadata?: () => void;
}) {
  const canOpenInfo = Boolean(hasMetadata && onOpenMetadata);
  const badge = canOpenInfo ? (
    <span
      aria-hidden="true"
      data-testid="theater-game-info-badge"
      className="pointer-events-none absolute -right-1 -top-1 text-white"
    >
      <Info
        className="h-4 w-4 [&_path]:stroke-ink [&_path]:[stroke-width:1px] [&_path]:[vector-effect:non-scaling-stroke] md:h-5 md:w-5"
        weight="fill"
      />
    </span>
  ) : null;

  if (iconUrl) {
    const image = (
      <img
        src={iconUrl}
        alt={canOpenInfo ? "" : `${title} icon`}
        title={title}
        className="h-10 w-10 rounded-lg object-cover md:h-12 md:w-12"
      />
    );

    if (canOpenInfo) {
      return (
        <button
          type="button"
          onClick={onOpenMetadata}
          data-testid="theater-game-info"
          className="relative shrink-0 cursor-pointer transition-opacity hover:opacity-80"
          aria-label="Show game info"
          title="Show game info"
        >
          {image}
          {badge}
        </button>
      );
    }

    return <div className="relative shrink-0">{image}</div>;
  }

  if (!canOpenInfo) return null;

  return (
    <button
      type="button"
      onClick={onOpenMetadata}
      data-testid="theater-game-info"
      className="relative flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-opacity hover:opacity-80 md:h-12 md:w-12"
      aria-label="Show game info"
      title="Show game info"
    >
      <Info className="h-5 w-5 text-white" weight="fill" />
    </button>
  );
}

function GameTitle({ title }: { title: string }) {
  return <h1 className="sr-only">{title}</h1>;
}

/** Share control only mounts when `shareUrl` is present so `useShareLink` always receives a real URL. */
function GameHeaderShareButton({
  shareUrl,
  shareTitle,
  title,
  classroomHref,
}: {
  shareUrl: string;
  shareTitle?: string;
  title: string;
  classroomHref: string;
}) {
  const { share, isSharing, canShare } = useShareLink({
    url: shareUrl,
    title: shareTitle ?? title,
  });
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      <div className="max-md:portrait:!hidden">
        <TheaterRailIconButton
          label="Share"
          aria-label="Share this game"
          icon={<ExportIcon weight="bold" size={24} />}
          onClick={() => share()}
          disabled={isSharing || !canShare}
        />
      </div>
      <Button
        onClick={() => setSheetOpen(true)}
        className="hidden rounded-full bg-brand-purple px-4 py-6 text-md font-bold text-white hover:bg-brand-purple-dark max-md:portrait:!flex"
        aria-label="Share this game"
        title="Share this game"
        data-testid="theater-portrait-share"
      >
        <ExportIcon weight="bold" size={24} />
      </Button>
      <GameShareSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        classroomHref={classroomHref}
        onShare={share}
        isSharing={isSharing}
        canShare={canShare}
      />
    </>
  );
}

export function GameHeader({
  title,
  iconUrl,
  hasVideo = false,
  isShowingVideo = false,
  onVideoToggle,
  hasMetadata = false,
  onOpenMetadata,
  shareUrl,
  shareTitle,
  onClose,
  documentHeading = true,
}: GameHeaderProps) {
  const { isFullscreen, toggleFullscreen } = useFullscreenContext();

  const handleToggleFullscreen = useCallback(async () => {
    await toggleFullscreen();
  }, [toggleFullscreen]);

  const resolvedShareUrl = shareUrl?.trim() ?? "";
  const showShareButton = resolvedShareUrl.length > 0;
  const classroomHref = showShareButton
    ? classroomShareUrlForPage(resolvedShareUrl, {
        title: shareTitle ?? title,
        body: DEFAULT_CLASSROOM_BODY,
      })
    : null;

  return (
    <div
      data-testid="game-header"
      className={cn(
        "relative flex h-full w-auto min-h-0 flex-col items-center justify-between gap-3 overflow-visible py-0 md:gap-4",
        Z_INDEX_CLASSES.GAME_THEATER_HEADER,
        "max-md:portrait:!h-auto max-md:portrait:!w-full max-md:portrait:!flex-row"
      )}
    >
      <div className="flex min-w-0 shrink-0 flex-col items-center gap-3 md:gap-4 max-md:portrait:!flex-row">
        {onClose && (
          <>
            <div className="max-md:portrait:!hidden">
              <TheaterRailIconButton
                label="Close"
                icon={<XIcon weight="bold" size={20} />}
                onClick={onClose}
              />
            </div>
            <Button
              type="button"
              onClick={onClose}
              className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-purple p-0 text-white hover:bg-brand-purple-dark max-md:portrait:!flex"
              aria-label="Close"
              title="Close"
              data-testid="theater-portrait-close"
            >
              <XIcon weight="bold" size={20} />
            </Button>
          </>
        )}
        <GameIcon
          title={title}
          iconUrl={iconUrl}
          hasMetadata={hasMetadata}
          onOpenMetadata={onOpenMetadata}
        />
        {documentHeading ? <GameTitle title={title} /> : null}
      </div>

      <TheaterRailTitle title={title} />

      <div className="flex shrink-0 flex-col-reverse items-center gap-3 max-md:portrait:!flex-row">
        {showShareButton && classroomHref && (
          <GameHeaderShareButton
            shareUrl={resolvedShareUrl}
            shareTitle={shareTitle}
            title={title}
            classroomHref={classroomHref}
          />
        )}
        {classroomHref && <GameClassroomButton href={classroomHref} />}

        <TheaterRailIconButton
          label={isFullscreen ? "Exit Big" : "Big"}
          icon={
            isFullscreen ? (
              <ArrowsInSimpleIcon weight="fill" size={24} strokeWidth={3} />
            ) : (
              <ArrowsOutSimpleIcon weight="fill" size={24} strokeWidth={3} />
            )
          }
          onClick={handleToggleFullscreen}
          slotClassName="hidden md:block"
        />

        {hasVideo && (
          <VideoToggleButton isShowingVideo={isShowingVideo} onVideoToggle={onVideoToggle} />
        )}
      </div>
    </div>
  );
}
